<template>
  <b-container fluid>
    <div class="row">
      <div class="col-lg-12">
        <h2 class="lz-home-heading">{{ title }}</h2>
      </div>
    </div>
    <div class="row" v-if="isAdmin && !loading">
      <div class="col-lg-12">
        <resource-toolbar
          v-model="editMode"
          :admin="isAdmin"
          @manage="OpenManageSections"
          @add="AddResource" />
      </div>
    </div>
    <div class="row" v-if="loading">
      <div class="col-lg-12">
        <b-skeleton width="30%" class="mb-4"></b-skeleton>
        <b-skeleton-img no-aspect height="132px" class="mb-3"></b-skeleton-img>
        <b-skeleton-img no-aspect height="132px"></b-skeleton-img>
      </div>
    </div>
    <div class="row" v-else>
      <div class="col-lg-12">
        <b-tabs pills card class="lz-home-inner-tabs">
          <b-tab
            v-for="tab in tabs.filter((tab) => tab.show || canEdit)"
            :key="tab.id"
            :active="tab.active"
            lazy
            @click="tabChanged(tab.id)">
            <template #title>
              <span :class="{ 'is-hidden-resource': !tab.show }">{{ tab.title }}</span>
            </template>
            <component
              :is="tab.component"
              :subSections="canEdit ? tab.subSections : VisibleSubSections(tab.subSections)"
              :showAsGroup="tab.group || false"
              :isAdmin="canEdit"
              @edit="EditResource"
              @toggle="ToggleResource" />
          </b-tab>
        </b-tabs>
      </div>
    </div>
    <resource-form-modal
      ref="resourceModal"
      tab="useful-links"
      :parentOptions="parentOptions"
      @saved="ResourceSaved" />
    <manage-sections-modal
      ref="manageSectionsModal"
      tab="useful-links"
      @changed="fetchResources" />
  </b-container>
</template>

<script>
import Services from '../../../services/main.service.vue'
import contentComponent from './content-component.vue'
import resourceFormModal from '../resource-form-modal.vue'
import manageSectionsModal from '../manage-sections-modal.vue'
import resourceToolbar from '../resource-toolbar.vue'

export default {
  name: 'HomeUsefulLinksModule',
  components: {
    contentComponent,
    resourceFormModal,
    manageSectionsModal,
    resourceToolbar
  },
  props: {
    title: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      editMode: false,
      activeSectionUid: null,
      rawSections: [],
      tabs: []
    }
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.hasAdminPrivileges
    },
    canEdit() {
      return this.isAdmin && this.editMode
    },
    parentOptions() {
      const options = []
      const walk = (nodes, depth) => {
        nodes.forEach((node) => {
          if (!node.link && !node.videoLink && !node.filesLink) {
            options.push({
              value: node.uid,
              text: `${'— '.repeat(depth)}${node.title}`
            })
            walk(node.children ?? [], depth + 1)
          }
        })
      }
      walk(this.rawSections, 0)
      return options
    }
  },
  mounted() {
    this.fetchResources()
  },
  methods: {
    async fetchResources() {
      try {
        this.loading = true
        const { status, data } = await Services.Home.GetHomeResources()
        if (status === 200) {
          const sections = data?.content?.['useful-links'] ?? []
          this.rawSections = sections
          const currentUid = this.activeSectionUid
          this.tabs = sections.map((section, index) => ({
            id: `tab_${section.uid}`,
            uid: section.uid,
            component: 'content-component',
            title: section.title,
            active: currentUid ? section.uid === currentUid : index === 0,
            show: section.show !== false,
            group: section.meta?.group === true,
            subSections: (section.children ?? []).map((child) =>
              this.mapNode(child, section.uid)
            )
          }))
          if (!this.tabs.some((tab) => tab.active) && this.tabs.length) {
            this.tabs[0].active = true
          }
          this.activeSectionUid = this.tabs.find((tab) => tab.active)?.uid ?? null
        }
      } catch (error) {
        console.error(error.message)
        this.$toasted.error('Failed to load useful links, please try again later.').goAway(3500)
        this.tabs = []
      } finally {
        this.loading = false
      }
    },
    mapNode(node, parentUid = null) {
      const item = {
        uid: node.uid,
        parentUid,
        title: node.title,
        show: node.show,
        sortOrder: node.sortOrder,
        meta: node.meta
      }
      if (node.link) {
        item.link = node.link
      }
      if (node.videoLink) {
        item.videoLink = node.videoLink
      }
      if (node.filesLink) {
        item.filesLink = node.filesLink
      }
      if (node.children?.length) {
        item.subSections = node.children.map((child) =>
          this.mapNode(child, node.uid)
        )
      }
      return item
    },
    VisibleSubSections(items = []) {
      return items
        .filter((itm) => itm.show !== false)
        .map((itm) =>
          itm.subSections
            ? { ...itm, subSections: this.VisibleSubSections(itm.subSections) }
            : itm
        )
    },
    AddResource() {
      if (!this.canEdit) {
        return
      }
      this.$refs.resourceModal.Open(null, { parentUid: this.activeSectionUid })
    },
    OpenManageSections() {
      if (!this.canEdit) {
        return
      }
      this.$refs.manageSectionsModal.Open(this.tabs)
    },
    EditResource(item) {
      if (!this.canEdit) {
        return
      }
      this.$refs.resourceModal.Open(item)
    },
    async ToggleResource(item) {
      if (!this.canEdit) {
        return
      }
      try {
        const { status, data } = await Services.Home.UpdateHomeResource(item.uid, {
          show: item.show === false
        })
        if (status === 200 && data?.success !== false) {
          this.fetchResources()
        } else {
          this.$toasted.error('Failed to update visibility, please try again later.').goAway(3500)
        }
      } catch (error) {
        console.error(error.message)
        this.$toasted.error('Failed to update visibility, please try again later.').goAway(3500)
      }
    },
    ResourceSaved() {
      this.fetchResources()
    },
    tabMounted() {
      const selectedTab = this.tabs.find((tab) => tab.active)
      if (selectedTab) {
        this.tabChanged(selectedTab.id)
      }
    },
    tabChanged(tabId) {
      const tab = this.tabs.find((itm) => itm.id === tabId)
      this.activeSectionUid = tab?.uid ?? this.activeSectionUid
      this.$root.$emit(`${tabId}_selected`)
    }
  }
}
</script>

<style scoped>
/* Shared home styling (toolbar, tabs, cards) lives in src/styles/logs.css. */
.is-hidden-resource {
  opacity: 0.55;
}
</style>
